import { Typography } from "@mui/material";
import { debounce } from "lodash"; // Import the debounce function from the lodash library
import { useEffect, useRef, useState } from "react";
import { RestrictedResourceLink } from "../RefrencesFetcher/ResourcesRendering/ProtectedResource";
import VideoPlayer from "../RefrencesFetcher/ResourcesRendering/VideoRendering";
import ReferenceReadStore from "../../../../services/ReferenceReadStore";

/**
 * Render a list of references with "new" badges for unopened items.
 * 
 * @param {refrences} Array The list of references to render.
 * @param {receiveRefreshBadgesTrigger} function A callback to receive a function that can be called to refresh the "new" badges. 
 */
const RefrencesRender = ({ refrences, receiveRefreshBadgesTrigger }) => {
  const handleResize = debounce(() => {
    const videoElements = document.querySelectorAll("video");

    videoElements.forEach(videoElement => {
      if (videoElement) {
        const { clientWidth, clientHeight } = videoElement;
        videoElement.setAttribute("width", clientWidth);
        videoElement.setAttribute("height", clientHeight);
      }
    });
  }, 100); // Set the debounce delay to 100ms

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Track newly added references (by hashed JSON key) and show an indicator next to them
  const prevPathsRef = useRef(new Set());
  const [newItems, setNewItems] = useState({});
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Helper: evaluate which refs are new (not opened in this session)
  const updateNewItems = (refs) => {
    if (!Array.isArray(refs)) return;
    const currentKeys = new Set(refs.map(r => ReferenceReadStore.keyFor(r)));
    const prevKeys = prevPathsRef.current;

    const added = [];
    refs.forEach(r => {
      const key = ReferenceReadStore.keyFor(r);
      if (key && !prevKeys.has(key)) {
        try {
          const opened = ReferenceReadStore.isOpened(r);
          if (!opened) added.push(key);
        } catch (e) {
          added.push(key);
        }
      }
    });

    if (added.length > 0) {
      setNewItems(prev => {
        const next = { ...prev };
        added.forEach(k => {
          next[k] = true;
        });
        return next;
      });
    }

    prevPathsRef.current = currentKeys;
  };

  useEffect(() => {
    updateNewItems(refrences);
  }, [refrences]);

  // Register refresh callback so parent (nextQuestion) can force a re-check
  useEffect(() => {
    if (!receiveRefreshBadgesTrigger) return;

    const refreshBadges = () => {
      // re-evaluate new items (in case sessionStorage changed)
      try {
        updateNewItems(refrences);
      } catch (e) { }

      setNewItems(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => {
          try {
            // try to find a matching reference object
            const found = Array.isArray(refrences)
              ? refrences.find(r => ReferenceReadStore.keyFor(r) === k)
              : null;
            const opened = found ? ReferenceReadStore.isOpened(found) : !!sessionStorage.getItem(k);
            if (opened) {
              delete next[k];
              return;
            }
          } catch (e) { }

          // Also clear badges for inline HTML references on refresh
          if (Array.isArray(refrences)) {
            const found = refrences.find(r => ReferenceReadStore.keyFor(r) === k);
            if (found) {
              if (found.refrence_html !== undefined && found.type !== "pdf") {
                // Mark inlined HTML reference as opened when refreshing badges
                try {
                  ReferenceReadStore.markOpened(found);
                } catch (e) {
                  console.error(e);
                }
                delete next[k];
              }
            } else {
              // if reference no longer exists, remove badge
              delete next[k];
            }
          }
        });
        return next;
      });
    };

    receiveRefreshBadgesTrigger(refreshBadges);
  }, [receiveRefreshBadgesTrigger, refrences]);

  // monitor visibility of the "new" badges themselves and show a floating
  // scroll indicator if any badge is out of view (better when the reference
  // content is large and the badge can be scrolled off-screen)
  useEffect(() => {
    const checkVisibility = () => {
      const badges = document.querySelectorAll('[aria-label="new-refrence-indicator"]');
      if (!badges || badges.length === 0) {
        setShowScrollIndicator(false);
        return;
      }
      let anyOut = false;
      for (const badge of badges) {
        // ignore badges that are hidden (we always render a placeholder)
        try {
          const cs = window.getComputedStyle(badge);
          if (cs && (cs.visibility === 'hidden' || parseFloat(cs.opacity || '0') === 0)) continue;
        } catch (e) {}
        const rect = badge.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
        if (!inView) {
          anyOut = true;
          break;
        }
      }
      setShowScrollIndicator(anyOut);
    };

    // initial check
    checkVisibility();

    // debounce scroll/resize checks
    const debounced = debounce(checkVisibility, 120);
    window.addEventListener("scroll", debounced, { passive: true });
    window.addEventListener("resize", debounced);

    // observe DOM mutations (badges may appear/disappear)
    const observer = new MutationObserver(debounced);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("scroll", debounced);
      window.removeEventListener("resize", debounced);
      observer.disconnect();
    };
  }, [newItems]);

  const SwitchRender = refrence => {
    if (refrence["refrence_html"] !== undefined && refrence["type"] && refrence["type"] === "pdf") {
      return (
        <div>
          <RestrictedResourceLink
            refrenceType="pdf"
            resourcePath={refrence.path}
            name={refrence.name}
            onOpened={() => handleOnOpened(refrence)}
            otherOptions={{ page: 1 }}
          />
          <div dangerouslySetInnerHTML={{ __html: refrence.refrence_html }} />
        </div>
      );
    }
    else if (refrence["refrence_html"] !== undefined) {
      return (
        <div dangerouslySetInnerHTML={{ __html: refrence["refrence_html"] }} />
      );
    } else if (refrence["type"] && refrence["type"] === "pdf") {
      return (
        <div>
          <RestrictedResourceLink
            refrenceType={"pdf"}
            resourcePath={refrence["path"]}
            name={refrence["name"]}
            onOpened={() => handleOnOpened(refrence)}
            otherOptions={{ page: 1 }}
          />
        </div>
      );
      // return <div> {IframeViewer(refrence["path"], refrence["name"])} </div>;
    } else if (refrence["type"] && refrence["type"] === "video") {
      return <VideoPlayer path={refrence["path"]} onOpened={() => handleOnOpened(refrence)} />;
      // let videoFullPath = process.env.PUBLIC_URL + refrence["image_path"];
      // if (refrence["path"].startsWith(".")) {
      //   videoFullPath =
      //     process.env.PUBLIC_URL + refrence["path"].substring(1);
      // }
      // return (
      //   <div>
      //     <video
      //       width="640"
      //       height="480"
      //       className="my-element"
      //       controls
      //       controlsList="nofullscreen nodownload"
      //     >
      //       <source src={videoFullPath} type="video/mp4" />
      //     </video>
      //   </div>
      // );
    } else if (refrence["image_path"] !== undefined) {
      let imageFullPath = process.env.PUBLIC_URL + refrence["image_path"];
      if (refrence["image_path"].startsWith(".")) {
        imageFullPath =
          process.env.PUBLIC_URL + refrence["image_path"].substring(1);
      }
      return (
        <div
          className="image-container"
          style={{ float: "right", marginBottom: "10px" }}
        >
          <img src={imageFullPath} alt="refrence" />
        </div>
      );
    } else if (refrence["type"] && refrence["type"] === "html") {
      // return <div> {IframeViewer(refrence["path"], refrence["name"])} </div>;
      return (
        <div>
          <RestrictedResourceLink
            refrenceType={"html"}
            resourcePath={refrence["path"]}
            name={refrence["name"]}
            onOpened={() => handleOnOpened(refrence)}
          />
        </div>
      );
    }
  };

  // Handler that child renderers call when a reference is opened (link clicked or video played)
  const handleOnOpened = (ref) => {
    try {
      ReferenceReadStore.markOpened(ref);
      const key = ReferenceReadStore.keyFor(ref);
      setNewItems(prev => {
        const next = { ...prev };
        if (next[key]) delete next[key];
        return next;
      });
    } catch (e) {
      // ignore
    }
  };

  return (
    <div style={{ textAlign: "right", padding: "12px 16px" }}>
      <Typography variant="h4" component="h1" align="right" gutterBottom>
        חומרי עזר
      </Typography>
      <div>
        {Array.isArray(refrences) && refrences.length > 0 ? (
          refrences.map(refrence => {
            const key = ReferenceReadStore.keyFor(refrence);
            return (
              <div
                key={key}
                data-ref-path={key}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 6 }}
              >
                <div style={{ marginLeft: 8 }}>{SwitchRender(refrence)}</div>
                {/* Badge placeholder: always render to reserve layout space; toggle visibility so content doesn't jump */}
                <div
                  aria-label="new-refrence-indicator"
                  title="מומלץ לקרוא את חומר העזר"
                  style={{
                    marginRight: 12,
                    background: "#d32f2f",
                    color: "white",
                    fontWeight: 700,
                    borderRadius: 8,
                    padding: "2px 6px",
                    fontSize: 12,
                    lineHeight: "16px",
                    marginLeft: 8,
                    cursor: "default",
                    // keep the element in layout but hide visually when not new
                    visibility: key && newItems[key] ? 'visible' : 'hidden',
                    opacity: key && newItems[key] ? 1 : 0,
                    transition: 'opacity 160ms ease'
                  }}
                >
                  חדש
                </div>
              </div>
            );
          })
        ) : (
          <Typography variant="body1" align="right" sx={{ color: "text.secondary", mt: 2 }}>
            אין חומרי עזר בפרק זה
          </Typography>
        )}
      </div>
      {showScrollIndicator && (
        <button
          title="יש חומרים חדשים - גלול אליהם"
          onClick={() => {
            // scroll to the first NEW BADGE that is out of view
            const badges = document.querySelectorAll('[aria-label="new-refrence-indicator"]');
            for (const badge of badges) {
              const rect = badge.getBoundingClientRect();
              const inView = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
              if (!inView) {
                badge.scrollIntoView({ behavior: "smooth", block: "center" });
                // after scrolling, re-check visibility a moment later
                setTimeout(() => {
                  try {
                    const ev = new Event('scroll');
                    window.dispatchEvent(ev);
                  } catch (e) { }
                }, 600);
                break;
              }
            }
          }}
          style={{
            position: "fixed",
            right: 20,
            bottom: 24,
            zIndex: 9999,
            background: "#1976d2DA",
            color: "white",
            border: "none",
            borderRadius: 24,
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            cursor: "pointer",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16 }}>↓</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>חומרים חדשים</span>
        </button>
      )}
    </div>
  );
};

export default RefrencesRender;
